import React, { useEffect, useState } from 'react';
import moment from 'moment';
import CustomTable from '../common/CustomTable';
import { formatDateOnly } from '../../utils/util';

interface InterestData {
  id: number;
  date: string;
  credit: number;
  debit: number;
  balance: number;
  days: number;
  interestAmount: number;
}

const InterestReport: React.FC = () => {
  const [filters, setFilters] = useState({
    party: '',
    startDate: '2025-07-01',
    endDate: '2025-07-31',
    type: '',
  });

  const [interestData, setInterestData] = useState<InterestData[]>([]);
  const [filteredData, setFilteredData] = useState<InterestData[]>([]);
  const [totalInterest, setTotalInterest] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    const data: InterestData[] = [
      {
        id: 1,
        date: '2025-07-02',
        credit: 500,
        debit: 0,
        balance: 500,
        days: 10,
        interestAmount: 25.0,
      },
      {
        id: 2,
        date: '2025-07-12',
        credit: 0,
        debit: 200,
        balance: 300,
        days: 5,
        interestAmount: 15.0,
      },
      {
        id: 3,
        date: '2025-07-20',
        credit: 0,
        debit: 300,
        balance: 0,
        days: 0,
        interestAmount: 0,
      },
    ];
    setInterestData(data);
  }, []);

  const handleSearch = () => {
    const filtered = interestData.filter(item => {
      const date = moment(item.date);
      return (
        (!filters.party || filters.party.trim() !== '') &&
        date.isBetween(filters.startDate, filters.endDate, 'day', '[]')
      );
    });

    setFilteredData(filtered);
    const total = filtered.reduce((sum, item) => sum + item.interestAmount, 0);
    setTotalInterest(total);
    setCurrentPage(0);
  };

  return (
    <div className="p-4 w-full mx-auto space-y-4">
      <h2 className="text-xl font-bold uppercase text-white">Interest</h2>

      {/* Filter section */}
      <div className="flex flex-wrap items-end gap-4 bg-blue-200 p-4 rounded-md">
        <div className="flex flex-col">
          <label className="text-sm font-medium">Party</label>
          <input
            type="text"
            value={filters.party}
            onChange={(e) => setFilters({ ...filters, party: e.target.value })}
            className="p-2 rounded border border-gray-300"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium">Starting Date</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            className="p-2 rounded border border-gray-300"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium">Ending Date</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            className="p-2 rounded border border-gray-300"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium">Type</label>
          <select
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            className="p-2 rounded border border-gray-300"
          >
            <option value="">Select Type</option>
            <option value="A">A</option>
            <option value="B">B</option>
          </select>
        </div>

        <button
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          onClick={handleSearch}
        >
          Search
        </button>

        <div className="ml-auto flex flex-col">
          <label className="text-sm font-medium">Total Interest</label>
          <input
            type="text"
            value={totalInterest.toFixed(2)}
            readOnly
            className="p-2 rounded border border-gray-300 text-right bg-white"
          />
        </div>
      </div>

      {/* Table section */}
      <CustomTable<InterestData>
        data={filteredData.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage)}
        columns={[
          {
            key: 'date',
            label: 'Date',
            render: (item : any) => formatDateOnly(item.date),
          },
          { key: 'credit', label: 'Credit' },
          { key: 'debit', label: 'Debit' },
          { key: 'balance', label: 'Balance' },
          { key: 'days', label: 'Days' },
          {
            key: 'interestAmount',
            label: 'Interest Amount',
            render: (item : any) => item.interestAmount.toFixed(2),
          },
        ]}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={setRowsPerPage}
        totalPages={Math.ceil(filteredData.length / rowsPerPage)}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        loading={false}
        getId={(item: any) => item.id}
        title=""
      />
    </div>
  );
};

export default InterestReport;
